import * as React from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'

import { Centered, Column, Form, Input, Loader, Modal } from '../../../../components'
import Button, { Sizes as ButtonSizes } from '../../../../components/Button'
import { FormGroup, FormHeader } from '../../../../components/Form'
import Padding, { Sizes as PaddingSizes } from '../../../../components/Padding'
import { fetchDeviceGroups } from '../../../../actions/deviceGroups'
import { fetchChains } from '../../../../actions/chains'
import { useAppDispatch } from '../../../../actions/types'
import { useAppSelector } from '../../../../reducers'
import { routes, Routes } from '../../../../constants'
import { decodeFilter, encodeFilter } from './searchString'
import type { ConnectionStatus, MigrationStatus, URLParams } from './types'
import type { DeviceGroup } from '../../../../types/DeviceMain'
import { fetchSingleStore } from '../../../../actions/stores'

interface FilterFormProps {
    isOpen?: boolean
    onClose?: () => void
}

function DeviceFilterForm({
    isOpen = false,
    onClose = () => null,
}: FilterFormProps) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { code, estate, search } = useParams<URLParams>()
    const { search: rawSearch } = useLocation()
    const history = useHistory()

    const deviceGroups = useAppSelector<DeviceGroup[]>((state) => state.device.displayGroups)
    const isFetchingDisplayGroups = useAppSelector(
        (state) => state.device.isFetchingDisplayGroups,
    )
    const chains = useAppSelector((state) => state.chains.chains)
    const isFetchingChains = useAppSelector((state) => state.chains.isWorking)

    const [isFetchingData] = React.useState<boolean>(false)

    // Flag for knowing if all the data needed for the default form values are updated
    const [hasLoadedDefaultValues, setHasLoadedDefaultValues] = React.useState<boolean>(false)
   
    /**
     * Update device groups and chains when rendering for the first time
     */
    React.useEffect(() => {
        const fetchAll = async () => {
            await Promise.all([
                dispatch(fetchDeviceGroups()),
                dispatch(fetchChains()),
            ])

            // Notify all data needed for the default form values
            // are now fully loaded
            setHasLoadedDefaultValues(true)
        }

        fetchAll()
    }, [dispatch])

    /**
     * Initial values for the filters form
     */
    const defaultFormValues = React.useMemo(() => {
        return {
            deviceGroups: [1] as number[],
            connectionStatus: ['online', 'offline'] as ConnectionStatus[],
            migration: ['migrated'] as MigrationStatus[],
            chains: chains.map(chain => chain.id),
        }
    }, [chains])

    /**
     * Form values
     */
    const [
        selectedDeviceGroups,
        setDeviceGroup,
    ] = React.useState<number[]>(defaultFormValues.deviceGroups)
    const [
        selectedStatus,
        setSelectedStatus,
    ] = React.useState<ConnectionStatus[]>(defaultFormValues.connectionStatus)
    const [
        selectedMigration,
        setSelectedMigration,
    ] = React.useState<MigrationStatus[]>(defaultFormValues.migration)
    const [
        selectedChains,
        setSelectedChains,
    ] = React.useState<number[]>(defaultFormValues.chains)
    /**
     * End of form values
     */

    // Set filter with values taken from the URL search params
    React.useEffect(() => {
        // Decode URL search string and get filters
        const {
            deviceGroups: urlDeviceGroups,
            connectionStatus: urlConnectionStatus,
            migration: urlMigration,
            chains: urlChains,
        } = decodeFilter(rawSearch)

        // Extract default filter values
        const {
            deviceGroups: defaultDeviceGroups,
            connectionStatus: defaultConnectionStatus,
            migration: defaultMigration,
            chains: defaultChains,
        } = defaultFormValues

        // For every element in the filter, take the URL value.
        // If no value is set in the URL, use the default values.
        const deviceGroups = urlDeviceGroups ?? defaultDeviceGroups
        const connectionStatus = urlConnectionStatus ?? defaultConnectionStatus
        const migration = urlMigration ?? defaultMigration
        const chains = urlChains ?? defaultChains

        // Update component's states
        setDeviceGroup(deviceGroups)
        setSelectedStatus(connectionStatus)
        setSelectedMigration(migration)
        setSelectedChains(chains)

        if (hasLoadedDefaultValues) {
            // Call backend to fetch store data with the select filters
            dispatch(fetchSingleStore({
                storeCode: code,
                deviceGroups,
                connectionStatus,
                migration,
                chains,
                deviceId: search,
            }))
        }
    }, [code, defaultFormValues, dispatch, hasLoadedDefaultValues, rawSearch, search])

    /**
     * Function to reset all filters to the default value
     */
    const resetFilters = () => {
        const {
            deviceGroups: defaultDeviceGroups,
            connectionStatus: defaultConnectionStatus,
            migration: defaultMigration,
            chains: defaultChains,
        } = defaultFormValues

        // Update states with default values
        setDeviceGroup(defaultDeviceGroups)
        setSelectedStatus(defaultConnectionStatus)
        setSelectedMigration(defaultMigration)
        setSelectedChains(defaultChains)
        
        // Close form
        onClose()

        // Replace URL with the default values
        replaceURLSearch({
            deviceGroups: defaultDeviceGroups,
            connectionStatus: defaultConnectionStatus,
            migration: defaultMigration,
            chains: defaultChains,
        })
    }

    const onChangeDeviceGroup = ({ target: { value } }: { target: { value: number[] } }) => {
        setDeviceGroup(value)
    }

    const onChangeConnectionStatusGroup =
        ({ target: { value } }: { target: { value: ConnectionStatus[] } }) => {
            setSelectedStatus(value)
        }

    const onChangeMigrationGroup =
        ({ target: { value } }: { target: { value: MigrationStatus[] } }) => {
            setSelectedMigration(value)
        }

    const onChangeChains = ({ target: { value } }: { target: { value: number[] } }) => {
        setSelectedChains(value)
    }

    /**
     * Replaces search string in the URL with the values in the parameters
     */
    const replaceURLSearch = ({
        deviceGroups,
        connectionStatus,
        migration,
        chains,
    }: {
        deviceGroups: number[],
        connectionStatus: ConnectionStatus[],
        migration: MigrationStatus[],
        chains: number[],
    }) => {
        const searchString = encodeFilter({
            deviceGroups,
            connectionStatus,
            migration,
            chains,
        })

        const url = Routes.buildRoute(routes.ESTATE_STORE, {
            estate,
            code,
            search: search ?? '',
        })
        history.replace([url, searchString].join('?'))
        onClose()
    }

    /**
     * Event for submitting form
     */
    const onSubmit = () => {
        replaceURLSearch({
            deviceGroups: selectedDeviceGroups,
            connectionStatus: selectedStatus,
            migration: selectedMigration,
            chains: selectedChains,
        })
    }

    const displayDeviceGroups = React.useMemo(() => deviceGroups.map((group) => {
        return {
            ...group,
            value: group.id,
        }
    }), [deviceGroups])

    const displayChains = React.useMemo(() => chains.map((chain) => {
        return {
            ...chain,
            value: chain.id,
        }
    }), [chains])

    const isFilterActive = !isEqual(defaultFormValues, {
        deviceGroups: selectedDeviceGroups,
        connectionStatus: selectedStatus,
        migration: selectedMigration,
        chains: selectedChains,
    })

    return (
        <Modal isOpen={isOpen} onClose={() => onClose()} minWidth="419px" scrollable>
            <Form>
                <FormHeader>
                    <Padding right bottom size={PaddingSizes.sm}>
                        <Column>{t('filters-device')}</Column>
                    </Padding>
                </FormHeader>
                <FormGroup title={t('Device groups')}>
                    {isFetchingDisplayGroups && <Centered horizontal><Loader /></Centered>}
                    {!isFetchingDisplayGroups && (
                        <Input
                            id="selectedGroups"
                            name="selectedGroups"
                            type="multiple"
                            onChange={onChangeDeviceGroup}
                            value={selectedDeviceGroups}
                            values={displayDeviceGroups}
                            column
                        />
                    )}
                </FormGroup>
                <FormGroup title={t('Status')}>
                    <Input
                        id="selectedStatus"
                        name="selectedStatus"
                        type="multiple"
                        onChange={onChangeConnectionStatusGroup}
                        value={selectedStatus}
                        values={[
                            { name: 'Online', value: 'online' },
                            { name: 'Offline', value: 'offline' },
                        ]}
                        column
                    />
                </FormGroup>
                <FormGroup title={t('devices-migration-status')}>
                    <Input
                        id="migrated"
                        name="migrated"
                        type="multiple"
                        onChange={onChangeMigrationGroup}
                        value={selectedMigration}
                        values={[
                            { name: t('Migrated'), value: 'migrated' },
                            { name: t('Not Migrated'), value: 'not-migrated' },
                        ]}
                    />
                </FormGroup>
                <FormGroup title={t('Chains')}>
                    {isFetchingChains && <Centered horizontal><Loader /></Centered>}
                    {!isFetchingChains && (
                        <Input
                            id="selectedChains"
                            name="selectedChains"
                            type="multiple"
                            onChange={onChangeChains}
                            value={selectedChains}
                            values={displayChains}
                            column
                        />
                    )}
                </FormGroup>
                <div className="modal-bottom">
                    <FormGroup>
                        <Column right width="100%">
                            <Button theme="secondary" onClick={() => onClose()} size={ButtonSizes.sm}>
                                {t('Cancel')}
                            </Button>
                            {isFilterActive && (
                                <Button
                                    theme="lightDanger"
                                    onClick={resetFilters}
                                    size={ButtonSizes.sm}
                                    radius="5"
                                >
                                    {t('Clear filters')}
                                </Button>
                            )}
                            <Button
                                onClick={onSubmit}
                                size={ButtonSizes.sm}
                                theme={isFetchingData ? 'loading' : 'forestGreen'}
                                disabled={isFetchingData}
                            >
                                {isFetchingData ? '...' : t('Filter')}
                            </Button>
                        </Column>
                    </FormGroup>
                </div>
            </Form>
        </Modal>
    )
}

export default DeviceFilterForm
